import { useState } from "react";

const SUBMIT_ADDR = "https://readdy.ai/api/form/db9172gkk00l6eltrno0";

const subjects = ["内容建议", "合作咨询", "问题反馈", "其他事项"];

export default function ContactForm() {
  const [status, setStatus] = useState<"idle" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const formEl = event.currentTarget;
    const formData = new FormData(formEl);

    const honeypot = String(formData.get("contact_alt") || "").trim();
    if (honeypot) {
      setStatus("success");
      setFormError("");
      formEl.reset();
      return;
    }

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === "contact_alt") return;
      if (typeof value === "string" && value.trim() === "") return;
      payload.append(key, String(value));
    });

    setSubmitting(true);
    try {
      const response = await fetch(SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });
      const responseText = await response.text();
      let parsed: {
        code?: string;
        message?: string;
        meta?: { message?: string; detail?: string };
      } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
      const isSpam = String(serverMsg || "").toLowerCase().includes("spam");
      const ok = response.ok && parsed?.code === "OK";

      if (ok && !isSpam) {
        setStatus("success");
        setFormError("");
        formEl.reset();
      } else {
        setStatus("error");
        setFormError(String(serverMsg || "提交失败，请稍后重试。"));
      }
    } catch {
      setStatus("error");
      setFormError("网络异常，提交未成功，请稍后重试。");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form
      data-readdy-form
      id="contact-feedback-form"
      onSubmit={handleSubmit}
      className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6"
    >
      <h3 className="font-heading font-semibold text-lg text-foreground-950 mb-1">留言与反馈</h3>
      <p className="text-sm text-foreground-500 mb-5">
        有想看的内容或使用建议？给我们留言，我们会认真阅读每一条反馈。
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm text-foreground-600 mb-1.5" htmlFor="cf-name">
            你的称呼
          </label>
          <input
            id="cf-name"
            name="name"
            required
            placeholder="请输入称呼"
            className="w-full h-11 px-3 rounded-md bg-background-100 border border-background-200 text-sm focus:border-primary-400 focus:bg-background-50 outline-none transition-colors"
          />
        </div>
        <div>
          <label className="block text-sm text-foreground-600 mb-1.5" htmlFor="cf-email">
            邮箱
          </label>
          <input
            id="cf-email"
            name="email"
            type="email"
            required
            placeholder="方便我们回复你"
            className="w-full h-11 px-3 rounded-md bg-background-100 border border-background-200 text-sm focus:border-primary-400 focus:bg-background-50 outline-none transition-colors"
          />
        </div>
      </div>

      <div className="mt-4">
        <label className="block text-sm text-foreground-600 mb-1.5" htmlFor="cf-subject">
          反馈类型
        </label>
        <select
          id="cf-subject"
          name="subject"
          required
          defaultValue=""
          className="w-full h-11 px-3 rounded-md bg-background-100 border border-background-200 text-sm focus:border-primary-400 focus:bg-background-50 outline-none transition-colors cursor-pointer"
        >
          <option value="" disabled>
            请选择反馈类型
          </option>
          {subjects.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
      </div>

      <div className="mt-4">
        <label className="block text-sm text-foreground-600 mb-1.5" htmlFor="cf-message">
          留言内容
        </label>
        <textarea
          id="cf-message"
          name="message"
          required
          maxLength={500}
          rows={5}
          placeholder="请输入你的建议或问题（不超过 500 字）"
          className="w-full px-3 py-2.5 rounded-md bg-background-100 border border-background-200 text-sm focus:border-primary-400 focus:bg-background-50 outline-none transition-colors resize-none"
        ></textarea>
      </div>

      {/* anti-spam guard field */}
      <input
        type="text"
        name="contact_alt"
        className="contact-alt-guard"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        readOnly
      />

      <div className="mt-5 flex flex-col sm:flex-row sm:items-center gap-4">
        <button
          type="submit"
          disabled={submitting}
          className="h-11 px-6 rounded-md bg-primary-500 text-background-50 text-sm font-medium flex items-center justify-center gap-2 hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60"
        >
          {submitting ? (
            <>
              <i className="ri-loader-4-line animate-spin"></i>
              提交中…
            </>
          ) : (
            <>
              <i className="ri-send-plane-line"></i>
              提交留言
            </>
          )}
        </button>

        {status === "success" && (
          <p className="text-sm text-secondary-700 flex items-center gap-1.5">
            <i className="ri-checkbox-circle-line text-primary-600"></i>
            提交成功，感谢你的反馈，我们会尽快阅读。
          </p>
        )}
        {status === "error" && (
          <p className="text-sm text-accent-700 flex items-center gap-1.5">
            <i className="ri-error-warning-line"></i>
            {formError}
          </p>
        )}
      </div>
    </form>
  );
}